<template>
  <div class="w-full">
    <!-- Users Table -->
    <table class="min-w-full divide-y divide-gray-700 rounded-lg overflow-hidden">
      <thead class="bg-gray-800">
      <tr>
        <th class="py-3 px-4 text-left"></th> <!-- Expand/collapse column -->
        <th class="py-3 px-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Name</th>
        <th class="py-3 px-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Email</th>
        <th class="py-3 px-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Metrics Count</th>
        <th class="py-3 px-4 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
      </tr>
      </thead>
      <tbody class="bg-gray-900 divide-y divide-gray-700">
      <template v-for="user in users" :key="user.id">
        <!-- Main user row -->
        <tr class="hover:bg-gray-800/60">
          <td class="py-3 px-4">
            <button
                @click="toggleUserMetrics(user.id)"
                class="p-1 hover:bg-gray-700 rounded"
            >
              <svg
                  class="w-5 h-5 transition-transform duration-200"
                  :class="{ 'rotate-90': expandedUsers.has(user.id) }"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
              >
                <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
              </svg>
            </button>
          </td>
          <td class="py-3 px-4 text-gray-300">{{ user.name }}</td>
          <td class="py-3 px-4 text-gray-400">{{ user.email }}</td>
          <td class="py-3 px-4 text-gray-400">{{ userMetricMap[user.id]?.length ?? 0 }}</td>
          <td class="py-3 px-4 text-right">
            <button
                @click="openMetricModal(user)"
                class="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Add Metric
            </button>
          </td>
        </tr>

        <!-- Expanded metrics section -->
        <tr v-if="expandedUsers.has(user.id)">
          <td colspan="5" class="p-0 bg-gray-800/30 border-b border-gray-700">
            <div class="border-l-2 border-blue-500 mx-4 my-3 rounded-r overflow-hidden">

              <!-- Panel header -->
              <div class="flex items-center justify-between px-4 py-2 bg-gray-800/60 border-b border-gray-700/60">
                <span class="text-xs font-semibold text-blue-400 uppercase tracking-wider">Metrics</span>
                <span class="text-xs text-gray-500">{{ userMetricMap[user.id]?.length ?? 0 }} added</span>
              </div>

              <!-- Metrics table -->
              <table class="min-w-full" v-if="userMetricMap[user.id]?.length">
                <thead class="bg-gray-800/40">
                <tr>
                  <th class="py-2 px-4 text-left text-xs font-medium text-gray-500 w-1/4">Type</th>
                  <th class="py-2 px-4 text-left text-xs font-medium text-gray-500">Value</th>
                  <th class="py-2 px-4 text-left text-xs font-medium text-gray-500">Count</th>
                  <th class="py-2 px-4 text-left text-xs font-medium text-gray-500">Date</th>
                  <th class="py-2 px-4 text-right text-xs font-medium text-gray-500"></th>
                </tr>
                </thead>
                <tbody class="divide-y divide-gray-700/40">
                <tr v-for="metric in userMetricMap[user.id]" :key="metric.id" class="hover:bg-gray-700/20">
                  <td class="py-2 px-4 text-gray-300 text-sm font-medium">{{ metric.type }}</td>
                  <td class="py-2 px-4 text-gray-300 text-sm">{{ metric.value }}</td>
                  <td class="py-2 px-4 text-gray-300 text-sm">{{ metric.count }}</td>
                  <td class="py-2 px-4 text-gray-400 text-sm">{{ formatDate(metric.dateOccurred) }}</td>
                  <td class="py-2 px-4 text-right">
                    <button
                        @click="deleteMetric(user.id, metric.id)"
                        class="text-red-500 hover:text-red-400 text-xs transition-colors"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
                </tbody>
              </table>

              <div v-else class="px-4 py-3 text-gray-500 italic text-sm">
                No metrics added yet
              </div>
            </div>
          </td>
        </tr>
      </template>
      </tbody>
    </table>

    <!-- Metric Modal -->
    <TransitionRoot appear :show="isModalOpen" as="template">
      <Dialog as="div" @close="closeMetricModal" class="relative z-10">
        <TransitionChild
            enter="duration-300 ease-out"
            enter-from="opacity-0"
            enter-to="opacity-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100"
            leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </TransitionChild>

        <div class="fixed inset-0 overflow-y-auto">
          <div class="flex min-h-full items-center justify-center p-4">
            <TransitionChild
                enter="duration-300 ease-out"
                enter-from="opacity-0 scale-95"
                enter-to="opacity-100 scale-100"
                leave="duration-200 ease-in"
                leave-from="opacity-100 scale-100"
                leave-to="opacity-0 scale-95"
            >
              <DialogPanel class="w-full max-w-md transform rounded-xl bg-gray-900 border border-gray-700 shadow-2xl transition-all">

                <!-- Header -->
                <div class="px-6 pt-6 pb-4 border-b border-gray-700">
                  <DialogTitle class="text-base font-semibold text-white">
                    Add Metric
                  </DialogTitle>
                  <p class="mt-0.5 text-sm text-gray-400">{{ selectedUser?.name }}</p>
                </div>

                <form @submit.prevent="submitMetric" class="px-6 py-5 space-y-4">

                  <!-- Metric Type -->
                  <div>
                    <label class="block text-sm font-medium text-gray-300 mb-1.5">Metric Type</label>
                    <select
                        v-model="newMetric.type"
                        class="w-full px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option :value="null" class="text-gray-500">Select a type</option>
                      <option
                          v-for="type in metricTypes"
                          :key="type.id"
                          :value="type"
                          class="text-white"
                      >
                        {{ type.name }}
                      </option>
                    </select>
                  </div>

                  <!-- Value + Count side by side -->
                  <div class="grid grid-cols-2 gap-3">
                    <div>
                      <label class="block text-sm font-medium text-gray-300 mb-1.5">Value</label>
                      <input
                          type="number"
                          v-model="newMetric.value"
                          placeholder="0"
                          class="w-full px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white text-sm placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-300 mb-1.5">Count</label>
                      <input
                          type="number"
                          v-model="newMetric.count"
                          placeholder="0"
                          class="w-full px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white text-sm placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <!-- Date -->
                  <div>
                    <label class="block text-sm font-medium text-gray-300 mb-1.5">Date</label>
                    <VueDatePicker
                        v-model="newMetric.date"
                        :enable-time-picker="false"
                        :format="dateFormat"
                        input-class-name="dp-custom-input"
                        class="mt-0"
                        auto-apply
                        dark
                    />
                  </div>

                  <!-- Actions -->
                  <div class="flex justify-end gap-2 pt-2">
                    <button
                        type="button"
                        @click="closeMetricModal"
                        class="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-800 border border-gray-700 rounded-lg hover:bg-gray-700 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                        type="submit"
                        :disabled="!newMetric.type"
                        class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Add Metric
                    </button>
                  </div>
                </form>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
  </div>
</template>

<script setup lang="ts">
import {ref, reactive, onMounted, defineProps, defineEmits} from 'vue';
import { Dialog, DialogPanel, DialogTitle, TransitionRoot, TransitionChild } from '@headlessui/vue';
import type { User } from '@/types/User';
import api from "@/services/api.ts";
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import type {ExternalMetric, MetricType} from "@/types/Metrics.ts";
import userMetrics from "@/components/UserMetrics.vue";

defineOptions({
  name: 'UserMetrics',
  inheritAttrs: false
})

const dateFormat = (date: string | Date | null | undefined) => {
  if (!date) return '-';

  return new Date(date).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
    day: 'numeric'
  });
};

const emit = defineEmits<{
  (e: 'save', data: any): void;
  (e: 'close'): void;
}>();

interface Props {
  users: User[];
}

const props = defineProps<Props>();

// State
const expandedUsers = ref<Set<string>>(new Set());
const metricTypes = ref<MetricType[]>([]);
const isModalOpen = ref(false);
const selectedUser = ref<User | null>(null);
const userMetricMap = ref<MetricsMap>({});

const newMetric = reactive({
  id: '',
  userId: null,
  metricId: null,
  type: null as MetricType | null,
  value: null,
  count: null,
  date: new Date()
});

interface MetricsMap {
  [userId: string]: ExternalMetric[]
}

const saveMetrics = async () => {
  return {
    metrics: userMetricMap.value,
    users: props.users
  }
}

defineExpose({
  saveMetrics
})

onMounted(() => {

});

onMounted(async () => {
    const response = await api.get<MetricType[]>('/metrics');
    metricTypes.value = response.data;
});


const getMetricsCountForUser = (userId: string): number => {
  return userMetrics.value.filter((metric: ExternalMetric) => metric.userId === userId).length;
};

const toggleUserMetrics = (userId: string) => {
  if (expandedUsers.value.has(userId)) {
    expandedUsers.value.delete(userId);
  } else {
    expandedUsers.value.add(userId);
  }
};

const openMetricModal = (user: User) => {
  selectedUser.value = user;
  isModalOpen.value = true;
};

const closeMetricModal = () => {
  isModalOpen.value = false;
  selectedUser.value = null;
  newMetric.type = null;
  newMetric.value = null;
};

const submitMetric = () => {
  if (!selectedUser.value) return;

  const metric: ExternalMetric = {
    id: null,
    userId: selectedUser.value.id,
    type: newMetric.type?.name ?? null,
    metricId: newMetric.type?.id ?? null,
    value: newMetric.value,
    count: newMetric.count,
    dateOccurred: newMetric.date.toString(),
    userEmail: selectedUser.value.email
  };

  if (!userMetricMap.value[selectedUser.value.id]) {
    userMetricMap.value[selectedUser.value.id] = [];
  }

  userMetricMap.value[selectedUser.value.id].push(metric);
  // Auto-expand the user's metrics after adding
  expandedUsers.value.add(selectedUser.value.id);
  closeMetricModal();
};

const deleteMetric = (userId: string, metricId: string) => {
  const metrics = userMetricMap.value[userId];

  if (metrics) {
    const updatedMetrics = metrics.filter(metric => metric.id !== metricId);

    if (updatedMetrics.length > 0) {
      userMetricMap.value[userId] = updatedMetrics; // Update the array if there are metrics left
    } else {
      delete userMetricMap.value[userId]; // Remove the user key if no metrics are left
    }
  }
};

const handleSave = (data: any) => {
  emit('save', userMetricMap.value )
};


const formatDate = (date: string | Date | null | undefined): string => {
  if (!date) return '-';

  try {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch {
    return '-';
  }
};
</script>

<style scoped>
.fixed.inset-0.overflow-y-auto {
  overflow: visible !important;
}

:deep(.dp-custom-input) {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  background-color: rgb(31 41 55); /* gray-800 */
  border: 1px solid rgb(55 65 81);  /* gray-700 */
  color: white;
  font-size: 0.875rem;
  outline: none;
}

:deep(.dp-custom-input:focus) {
  border-color: transparent;
  box-shadow: 0 0 0 2px rgb(59 130 246); /* ring-blue-500 */
}
</style>